import { useMutation, useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { useSearchParams } from "react-router-dom"
import GameScreenLayout from "@/components/GameScreenLayout"
import GeoPointsList from "@/components/geo/GeoPointsList"
import { normalizeGeoPoint } from "@/components/geo/geoPointHelpers"
import { usePlayerGpsState } from "@/hooks/usePlayerGpsState"
import {
  getCardShopCatalog,
  getCardShopPoints,
  purchaseCardShopOffer,
} from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { ELEMENT_NAMES, STYLE_ICONS } from "@/constants/guardians"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"

export default function CardShopPage() {
  const [params, setParams] = useSearchParams()
  const pointId = params.get("pointId")
  const gps = usePlayerGpsState()
  const { syncGameState, bumpCards, passport } = useGuardiansPassport()
  const [selectedPoint, setSelectedPoint] = useState<string | null>(pointId)

  const pointsQuery = useQuery({
    queryKey: ["card-shop-points"],
    queryFn: getCardShopPoints,
  })

  const catalogQuery = useQuery({
    queryKey: ["card-shop-catalog", selectedPoint, gps?.lat, gps?.lng],
    queryFn: () =>
      getCardShopCatalog({
        pointId: selectedPoint!,
        playerLat: gps!.lat,
        playerLng: gps!.lng,
      }),
    enabled: Boolean(selectedPoint && gps),
  })

  const purchase = useMutation({
    mutationFn: (offerId: string) =>
      purchaseCardShopOffer({
        pointId: selectedPoint!,
        offerId,
        playerLat: gps!.lat,
        playerLng: gps!.lng,
      }),
    onSuccess: async () => {
      await syncGameState()
      bumpCards()
      void catalogQuery.refetch()
      void pointsQuery.refetch()
    },
  })

  const points = (pointsQuery.data ?? []).map((p) =>
    normalizeGeoPoint({
      id: p.id,
      name: p.name,
      lat: p.lat,
      lng: p.lng,
      radiusM: p.radiusM,
      meta: `Осталось ${p.purchasesRemaining}/${p.dailyLimit}`,
    })
  )

  function openShop(id: string) {
    setSelectedPoint(id)
    setParams({ pointId: id })
  }

  return (
    <GameScreenLayout title="Магазины карт" backTo="/play/map">
      <p className="mb-2 text-xs text-guardians-muted">
        Монеты: {passport?.coins ?? 0}
      </p>

      {!selectedPoint ? (
        pointsQuery.isLoading ? (
          <p className="text-sm text-guardians-muted">Загрузка точек…</p>
        ) : (
          <GeoPointsList
            points={points}
            gps={gps}
            highlightId={pointId}
            renderActions={({ point, inRange }) => (
              <button
                type="button"
                disabled={!inRange}
                onClick={() => openShop(point.id)}
                className="rounded-lg border border-white/20 px-3 py-1.5 text-xs disabled:opacity-40"
              >
                Каталог
              </button>
            )}
          />
        )
      ) : (
        <div className="space-y-3">
          <button
            type="button"
            className="text-xs text-guardians-accent"
            onClick={() => {
              setSelectedPoint(null)
              setParams({})
            }}
          >
            ← Все лавки
          </button>
          {catalogQuery.isLoading ? (
            <p className="text-sm text-guardians-muted">Каталог…</p>
          ) : (
            <ul className="space-y-2">
              {(catalogQuery.data?.offers ?? []).map((offer) => (
                <li
                  key={offer.id}
                  className="flex items-center justify-between rounded-xl border border-white/10 p-3 text-sm"
                >
                  <span>
                    {ELEMENT_NAMES[offer.element]} {STYLE_ICONS[offer.style]}
                  </span>
                  <button
                    type="button"
                    disabled={purchase.isPending}
                    onClick={() => purchase.mutate(offer.id)}
                    className="rounded-lg bg-guardians-accent px-3 py-1 text-xs text-guardians-bg"
                  >
                    {offer.priceCoins} 🪙
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {purchase.isError || catalogQuery.isError ? (
        <p className="mt-3 text-sm text-red-300">
          {getGameApiErrorMessage(
            purchase.error ?? catalogQuery.error,
            "Ошибка лавки"
          )}
        </p>
      ) : null}
    </GameScreenLayout>
  )
}
